import { IconType } from '@assembly-js/design-system';
import { Collapse } from '@mui/material';
import clsx from 'clsx';
import { PropsWithChildren, useEffect, useState } from 'react';

import { NotificationBadge } from 'src/components/NotificationBadge';
import { useMediaQuery } from 'src/hooks/useMediaQuery';
import { useNotificationBadgeData } from 'src/hooks/useNotificationBadgeData';

import {
  SidebarListItemContainer,
  SidebarListItemIcon,
  SidebarListItemText,
} from './SidebarListItem';

const FolderOpenIndicator = ({
  className,
  isOpen,
}: {
  className?: string;
  isOpen: boolean;
}) => (
  <SidebarListItemIcon
    className={className}
    iconName="caret-down"
    iconSettings={{
      className: clsx(
        'transition-transform duration-200',
        isOpen ? 'rotate-0' : '-rotate-90',
      ),
    }}
  />
);

interface SidebarFolderItemProps {
  htmlId: string;
  label: string;
  iconName?: IconType;
  isOpen: boolean;
  notificationCount?: number;
}

export const SidebarFolderItem = ({
  htmlId,
  label,
  iconName,
  isOpen,
  notificationCount = 0,
  children,
}: PropsWithChildren<SidebarFolderItemProps>) => {
  const isSmallMobile = useMediaQuery('xs', 'max');
  const { backgroundColor, textColor } = useNotificationBadgeData(true);
  const [isFolderOpen, setIsFolderOpen] = useState(isOpen);
  useEffect(() => {
    setIsFolderOpen(isOpen);
  }, [isOpen]);

  return (
    <>
      <div className="group">
        <SidebarListItemContainer
          htmlId={htmlId}
          handleClick={() => setIsFolderOpen((prev) => !prev)}
        >
          <>
            <SidebarListItemIcon
              className={clsx(
                'min-w-6 flex',
                !isSmallMobile && 'group-hover:hidden',
              )}
              iconName={iconName}
            />
            {!isSmallMobile && (
              <FolderOpenIndicator
                className="min-w-6 hidden group-hover:flex"
                isOpen={isFolderOpen}
              />
            )}
          </>
          <SidebarListItemText className="grow-0" text={label} />
          {isSmallMobile && (
            <FolderOpenIndicator
              className="ml-1.5 min-w-0"
              isOpen={isFolderOpen}
            />
          )}
          {notificationCount > 0 && (
            <div className="ml-auto">
              <NotificationBadge
                htmlId={`${htmlId}-notification-badge`}
                isSidebar
                content={notificationCount}
                backgroundColor={backgroundColor}
                textColor={textColor}
              />
            </div>
          )}
        </SidebarListItemContainer>
      </div>
      <Collapse
        classes={{ wrapper: '-my-2' }}
        appear={false} // disable animation on first mount
        enter={false} // disable enter animation
        exit={false} // disable exit animation
        in={isFolderOpen}
        timeout="auto"
        unmountOnExit
      >
        {children}
      </Collapse>
    </>
  );
};
